import { useEffect, useRef } from 'react';

import { Box } from '@chakra-ui/react';

export const PulseWaveLoader = ({
  size = 180,
  color = '0, 150, 255', // RGB
}: {
  size?: number | [number, number];
  color?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const resolvedSize = Array.isArray(size) ? size[0] : size;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = resolvedSize / 2;
    const centerY = resolvedSize / 2;

    const rings: Array<{ radius: number; count: number }> = [];
    const ringCount = 7;
    const maxRadius = resolvedSize * 0.38;

    for (let r = 1; r <= ringCount; r++) {
      rings.push({
        radius: (r / ringCount) * maxRadius,
        count: 24 + r * 6,
      });
    }

    const easeInOut = (t: number) =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

    let frame = 0;
    let animationId: number;

    const animate = () => {
      ctx.clearRect(0, 0, resolvedSize, resolvedSize);

      const scanHeight = resolvedSize * 0.5;
      const scanY = centerY + Math.sin(frame * 0.02) * (resolvedSize * 0.3);

      rings.forEach((ring) => {
        for (let i = 0; i < ring.count; i++) {
          const angle = (i / ring.count) * Math.PI * 2;
          const x = centerX + Math.cos(angle) * ring.radius;
          const y = centerY + Math.sin(angle) * ring.radius;

          const dist = Math.abs(y - scanY);
          let opacity = 0.1; // Base opacity so all dots are visible

          if (dist < scanHeight) {
            const waveOpacity = 1 - dist / scanHeight;
            opacity = 0.1 + easeInOut(waveOpacity) * 0.9; // Enhanced opacity in wave area
          }

          if (opacity > 0.05) {
            ctx.beginPath();
            ctx.arc(x, y, 1.5 + opacity * 2, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${color}, ${opacity})`;
            ctx.fill();
          }
        }
      });

      frame++;
      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(animationId);
  }, [resolvedSize, color]);

  const displaySize = Array.isArray(size) ? size : [size, size];

  return (
    <Box
      width={[`${displaySize[0]}px`, `${displaySize[1]}px`]}
      height={[`${displaySize[0]}px`, `${displaySize[1]}px`]}
      display='flex'
      alignItems='center'
      justifyContent='center'
    >
      <canvas ref={canvasRef} width={resolvedSize} height={resolvedSize} />
    </Box>
  );
};
